import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import "../CSS/InviteUserDialog.css";
import {
  getAirportCode,
  getAirportData,
  validateEmail,
} from "../../../../utils/utils";
import { useMutation } from "@tanstack/react-query";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { ACCOUNT_ROLE_STATUS } from "../../../../utils/constants";
import { StorageContext } from "../../../../services/StorageProvider";

const InviteUserDialog = ({
  open,
  onClose,
  onClickAdd,
  isLoading,
  airlineData,
}) => {
  const { airportData } = useContext(StorageContext);
  const { searchCommercialAirlineProfile } = useAirlineAdminApi();
  const [isAddingManually, setIsAddingManually] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [selectedAirlineAccess, setSelectedAirlineAccess] = useState(null);
  const [selectedAirports, setSelectedAirports] = useState([]);
  const [addingUserProfile, setAddingUserProfile] = useState(null);
  const [showSelectContainer, setShowSelectContainer] = useState(false);

  const searchCommercialAirlineProfileMutation = useMutation({
    mutationFn: ({ data }) => searchCommercialAirlineProfile(data),
    onSuccess: (response) => {
      setAddingUserProfile(response);
      setFirstName(response?.firstName);
      setLastName(response?.lastName);
      setIsAddingManually(true);
    },
    onError: () => {
      setIsAddingManually(true);
    },
  });

  useEffect(() => {
    setDefaultValue();
  }, [open]);

  const setDefaultValue = () => {
    setAddingUserProfile(null);
    setIsAddingManually(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setSelectedAccountType(null);
    setSelectedAirlineAccess(null);
    setSelectedAirports([]);
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleAirlineAccessChange = (event) => {
    setSelectedAirlineAccess(event.target.value);
  };

  const handleAccountTypeChange = (event) => {
    setSelectedAccountType(event.target.value);
  };

  const handleAirportToggle = (airportId) => {
    setSelectedAirports((prevSelected) => {
      if (prevSelected.includes(airportId)) {
        if (prevSelected.length === 1) {
          return prevSelected;
        }
        return prevSelected.filter((id) => id !== airportId);
      } else {
        return [...prevSelected, airportId];
      }
    });
  };

  const handleAddClick = () => {
    if (!isAddingManually) {
      searchCommercialAirlineProfileMutation.mutate({
        data: {
          email: email,
        },
      });
    } else {
      onClickAdd(
        firstName,
        lastName,
        email,
        selectedAccountType,
        selectedAirlineAccess,
        selectedAirports
      );
    }
  };

  const isValidToAdd = () => {
    return isAddingManually
      ? !firstName.trim() ||
          !lastName.trim() ||
          !selectedAccountType ||
          !selectedAirlineAccess ||
          selectedAirports.length <= 0
      : !email.trim() || !validateEmail(email);
  };

  return (
    <Dialog
      open={open ?? false}
      onClose={onClose}
      PaperProps={{
        className: isAddingManually
          ? "invite-user-manually-dialog-container"
          : "invite-user-dialog-container",
        sx: { borderRadius: "1px" },
      }}
    >
      <DialogContent>
        <CloseOutlined className="invite-user-dialog-close" onClick={onClose} />
        <div className="invite-user-dialog-content">
          <div className="invite-user-dialog-title">Invite Member</div>
          {isAddingManually && (
            <>
              <div className="invite-user-input-container">
                <label>First Name</label>
                {addingUserProfile ? (
                  <div className="invite-user-non-editable-field">
                    {addingUserProfile?.firstName}
                  </div>
                ) : (
                  <input
                    name="invite-user-first-name"
                    placeholder="Enter first name"
                    variant="outlined"
                    value={firstName}
                    onChange={handleFirstNameChange}
                  />
                )}
              </div>
              <div className="invite-user-input-container">
                <label>Last Name</label>
                {addingUserProfile ? (
                  <div className="invite-user-non-editable-field">
                    {addingUserProfile?.lastName}
                  </div>
                ) : (
                  <input
                    name="invite-user-last-name"
                    placeholder="Enter last name"
                    variant="outlined"
                    value={lastName}
                    onChange={handleLastNameChange}
                  />
                )}
              </div>
            </>
          )}
          <div className="invite-user-input-container">
            <label>Email</label>
            <input
              name="invite-user-email"
              placeholder="Enter email"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
            />
          </div>
          {isAddingManually && (
            <>
              <div className="invite-user-input-container">
                <label>Airline Account Type</label>
                <Select
                  id="invite-user-select-container"
                  className="invite-user-dialog-select-container"
                  value={selectedAccountType}
                  onChange={handleAccountTypeChange}
                  renderValue={(selected) =>
                    ACCOUNT_ROLE_STATUS.find((type) => type.value === selected)
                      ?.name
                  }
                >
                  {ACCOUNT_ROLE_STATUS.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <ListItemText primary={type.name} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="invite-user-input-container">
                <label>Airline Access</label>
                <Select
                  id="invite-user-select-container"
                  className="invite-user-dialog-select-container"
                  value={selectedAirlineAccess}
                  onChange={handleAirlineAccessChange}
                  renderValue={(selected) =>
                    airlineData?.data?.find(
                      (airline) => airline?.id === selected
                    )?.airlineName
                  }
                >
                  {airlineData?.data?.map((airline) => (
                    <MenuItem key={airline?.id} value={airline?.id}>
                      <ListItemText primary={airline?.airlineName} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="invite-user-input-container">
                <label>Airport Access</label>
                <Select
                  id="invite-admin-fbo-airport"
                  className="invite-user-dialog-select-container"
                  multiple
                  value={selectedAirports}
                  renderValue={(selected) =>
                    selected.length > 0
                      ? selected
                          .map((id) => {
                            const airport = getAirportData(airportData, id);
                            return getAirportCode(airport);
                          })
                          .join(", ")
                      : "Select Airports"
                  }
                  open={showSelectContainer}
                  onOpen={() => setShowSelectContainer(true)}
                  onClose={() => setShowSelectContainer(false)}
                >
                  <div className="invite-admin-select-list-containe">
                    {airportData?.map((airport) => (
                      <MenuItem
                        key={airport?.id}
                        onClick={() => handleAirportToggle(airport?.id)}
                      >
                        <Checkbox
                          checked={selectedAirports.includes(airport?.id)}
                        />
                        <ListItemText
                          primary={`${airport?.name} - ${getAirportCode(
                            airport
                          )}`}
                        />
                      </MenuItem>
                    ))}
                  </div>
                  <div className="invite-admin-select-button-container">
                    <Button
                      variant="contained"
                      color="send"
                      onClick={() => setShowSelectContainer(false)}
                    >
                      Add
                    </Button>
                  </div>
                </Select>
              </div>
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={!isLoading ? handleAddClick : null}
            endIcon={
              isLoading || searchCommercialAirlineProfileMutation?.isPending ? (
                <CircularProgress size={30} color="inherit" />
              ) : null
            }
            style={{ marginTop: "20px" }}
            disabled={isValidToAdd()}
          >
            Add
          </Button>
          {!isAddingManually && (
            <Button
              className="add-manually"
              onClick={() => setIsAddingManually(true)}
            >
              Add Manually
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
